import { useState, Fragment } from "react";

import { useAppDispatch, useAppSelector } from "@/store/hooks";
import { agenciesCreate } from "@/store/agencies";

import { User } from "@/types";

import AppBox from "@/components/AppBox";
import AppInput from "@/components/AppInput";
import AppButton from "@/components/AppButton";
import Autocomplete from "@mui/material/Autocomplete";

interface AgencyModalProps {
  onSaveAgency: () => void;
}

const AgencyModal: React.FC<AgencyModalProps> = ({ onSaveAgency }) => {
  const dispatch = useAppDispatch();
  const { status } = useAppSelector((state) => state.agencies);
  const { users } = useAppSelector((state) => state.users);

  const [form, setForm] = useState({
    name: "",
    email: "",
    user_id: 0,
    email_admin: "",
  });

  // Handle input change
  const handleChange =
    (field: keyof typeof form) => (value: string | number) => {
      setForm((state) => ({
        ...state,
        [field]: value,
      }));
    };

  // Handle saving agency
  const handleSaveAgency = async () => {
    await dispatch(agenciesCreate({ data: form }));
    setForm({ name: "", email: "", user_id: 0, email_admin: "" });
    onSaveAgency();
  };

  // Handle setting the agency admin
  const handleSetAgencyAdmin = (
    _event: React.SyntheticEvent, 
    value: User | null
  ) => {
    if (value) {
      setForm((state) => ({
        ...state,
        user_id: value.id,
      }));
    }
  };

  return (
    <AppBox column gap={4} fullWidth>
      <Fragment>
        {/* Name Input */}
        <AppInput
          type="text"
          label="Name"
          fullWidth
          onChange={(value) => handleChange("name")(value)}
          value={form.name}
        />

        {/* Email Input */}
        <AppInput
          type="email"
          label="Email"
          fullWidth
          onChange={(value) => handleChange("email")(value)}
          value={form.email}
          onPressEnter={handleSaveAgency}
          onBlur={() =>
            setForm((state) => ({
              ...state,
              email_admin: form.email.trim(),
            }))
          }
        />

        <AppBox className="text-sm font-semibold">Agency admin:</AppBox>

        {/* Autocomplete for Admin Selection */}
        <Autocomplete
          disablePortal
          options={users.filter((user) => !user.deleted_at)}
          getOptionLabel={(option) => `${option.name} (${option.email})`}
          onChange={handleSetAgencyAdmin}
          renderInput={(params) => (
            <AppInput
              {...params}
              type="text"
              label="Pick from users"
              fullWidth
              value={form.name}
            />
          )}
        />

        {/* Divider */}
        <AppBox className="text-center my-2 text-gray-500">
          -- OR create new user just by email --
        </AppBox>

        {/* Agency Admin Email Input */}
        <AppInput
          type="email"
          label="Agency Admin Email"
          fullWidth
          onChange={(value) => handleChange("email_admin")(value)}
          value={form.email_admin}
          onPressEnter={handleSaveAgency}
        />
      </Fragment>

      {/* Save Button */}
      <AppBox className="flex justify-end mt-4">
        <AppButton onClick={handleSaveAgency} isLoading={status === "loading"}>
          Save Agency
        </AppButton>
      </AppBox>
    </AppBox>
  );
};

export default AgencyModal;